import { InShort, RelatedPages } from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { Chevron, mkLink } from "@marketing/components/MkButton";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "Is our conversation data used to train AI models?",
		a: "No. Every request to an AI model that can carry shopper or store data is sent with data collection refused, on every channel.",
	},
	{
		q: "Where is our data hosted?",
		a: "Our application and database run on servers in Amsterdam, in the European Union, and product search embeddings are computed in Google Cloud's Netherlands region. AI models and speech are called through providers listed on our subprocessors page, some of them in the United States.",
	},
	{
		q: "Who can see our shoppers' conversations?",
		a: "The members of your team you invite, in your inbox. At Hephon, production access is limited to named staff. Photos and voice notes shoppers send are served only to your signed-in team.",
	},
	{
		q: "How are our platform credentials stored?",
		a: "API keys, tokens and webhook secrets you give us are encrypted with AES-256-GCM, each bound to the record it belongs to, and never shown again after you save them.",
	},
	{
		q: "How long are conversations kept?",
		a: "For the retention period you set, from 7 days to 10 years, 365 days by default. A daily job deletes older conversations with their messages, baskets and calls.",
	},
	{
		q: "Can shoppers ask for their data to be deleted?",
		a: "Yes. Deletion requests remove a shopper's conversations and details, and your team can follow each request in the dashboard or through the API.",
	},
	{
		q: "Is Hephon Commerce ISO 27001 or SOC 2 certified?",
		a: "No. Hephon Commerce holds no ISO or SOC certification. We answer security questionnaires and go through your requirements with your team instead.",
	},
	{
		q: "Is it PDPL compliant?",
		a: "We do not claim PDPL compliance, which depends on the whole processing, including yours. We explain how we handle data under Saudi Arabia's and the UAE's data protection laws, and go through your requirements with your team before launch.",
	},
];

/** Four promises, stated large on the first screen. */
const PROMISES = [
	{ value: "No", label: "conversation ever trains an AI model" },
	{ value: "Server", label: "enforces the products you never sell" },
	{ value: "AES-256", label: "encrypts every credential you give us" },
	{ value: "Shopper", label: "confirms every order, every time" },
];

const PROTECTION = [
	{
		title: "No training on your data",
		text: "Every AI request that can carry conversation data goes out with data collection refused.",
	},
	{
		title: "Rules on the server",
		text: "Products you never sell are removed before the assistant sees the catalog, so no prompt can bring them back.",
	},
	{
		title: "Encrypted secrets",
		text: "Platform credentials and webhook secrets are encrypted with AES-256-GCM, each bound to its own record.",
	},
	{
		title: "Invite-only access",
		text: "Accounts are created by invitation only, with roles per store and team.",
	},
	{
		title: "Passkeys",
		text: "Your team can sign in with passkeys instead of passwords.",
	},
	{
		title: "Hosted in Europe",
		text: "Application and database servers in Amsterdam; product embeddings in Google Cloud's Netherlands region.",
	},
];

const PRIVACY = [
	{
		title: "No shopper accounts with us",
		text: "Shoppers talk to your store. Their conversations belong to you.",
	},
	{
		title: "Deletion on request",
		text: "A shopper can ask for their data to be deleted, and your team follows each request in the dashboard.",
	},
	{
		title: "Private media",
		text: "Photos and voice notes shoppers send are served only to your signed-in team.",
	},
];

const LEGAL = [
	{ label: "Data processing agreement", href: "/legal/dpa" },
	{ label: "Subprocessors", href: "/legal/subprocessors" },
	{ label: "Privacy policy", href: "/legal/privacy" },
	{ label: "Terms of service", href: "/legal/terms" },
];

const STEPS = [
	{
		title: "Send us your requirements",
		text: "Your security questionnaire, data residency needs and the laws that apply to you, such as the Saudi or UAE PDPL.",
	},
	{
		title: "Go through them together",
		text: "We answer each point with your team, with the data processing agreement and the subprocessors list.",
	},
	{
		title: "Set your controls",
		text: "Retention period, masking, team roles, product rules and who may open the assistant, before shoppers see it.",
	},
];

const RELATED = [
	{ href: "/security/pdpl", label: "PDPL and your data" },
	{ href: "/integrations", label: "Integrations" },
	{ href: "/how-it-works", label: "How it works" },
	{ href: "/saudi-arabia", label: "For Saudi Arabia" },
	{ href: "/uae", label: "For the UAE" },
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
];

export const Route = createFileRoute("/_marketing/security")({
	head: () =>
		marketingHead({
			title: "AI Chatbot Security and Data Handling | Hephon Commerce",
			description:
				"AI chatbot security for retailers: no conversation trains a model, EU servers, encrypted credentials, deletion on request, product rules on the server.",
			path: "/security",
			breadcrumb: ["Security"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: SecurityPage,
});

function SecurityPage() {
	return (
		<>
			<PageIntro
				kicker="Your customers' trust, kept."
				title="Security and data handling for your AI shopping assistant"
				lead="An AI assistant talks to your customers and touches your catalog and orders. This is how we keep that safe, in plain words."
			>
				<dl className="mx-auto grid max-w-4xl grid-cols-2 gap-x-6 gap-y-10 pb-12 text-start sm:pb-16 lg:grid-cols-4">
					{PROMISES.map((item) => (
						<div key={item.label} className="flex flex-col">
							<dt className="order-last mt-2 text-(--mk-muted) text-[15px] leading-snug">
								{item.label}
							</dt>
							<dd className="font-semibold text-[clamp(1.75rem,3.4vw,2.5rem)] leading-none tracking-[-0.04em]">
								{item.value}
							</dd>
						</div>
					))}
				</dl>
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce never uses a conversation to train an AI
					model: every model request is sent with data collection
					refused. Its application and database run on servers in the
					European Union, platform credentials are encrypted, and
					shopper data is kept for the period the retailer sets and
					deleted on request.
				</p>
				<p>
					Products a retailer never sells are removed by the server
					before the assistant sees the catalog, and no order is
					placed without the shopper's confirmation. Hephon Commerce
					holds no ISO or SOC certification and does not claim PDPL
					compliance.
				</p>
			</InShort>

			<Section
				id="protection-title"
				title="Safe by design, not by policy alone."
			>
				<DetailGrid items={PROTECTION} />
			</Section>

			<Article tone="mist">
				<h2 id="training">
					Are our conversations used to train AI models?
				</h2>
				<p>
					No. Every request to an AI model that can carry a shopper's
					words, your catalog or a basket goes out with data
					collection denied, so it is routed only to model hosts that
					do not store or train on it. This holds on every channel:
					your app, your website, WhatsApp and calls. Our own test
					benches read only test conversations, never your shoppers'
					real ones, and a check in our build pipeline keeps it that
					way.
				</p>

				<h2 id="where">Where is the data stored and processed?</h2>
				<p>
					The application and its database run on servers in
					Amsterdam, in the European Union, and product search
					embeddings are computed in Google Cloud's Netherlands
					region. Writing a reply, turning speech into text and
					checking search results need AI providers, and some of them
					are in the United States. Each one is named on the{" "}
					<a href="/legal/subprocessors">subprocessors page</a> with
					what it sees. Data is not stored in Saudi Arabia or the UAE
					today. If you need it to be, tell us and we will go through
					it with your team.
				</p>

				<h2 id="who">Who can see our shoppers' conversations?</h2>
				<p>
					Your team, in your inbox. Accounts are created by invitation
					only, with roles, and your team can sign in with passkeys.
					Every conversation, voice note and call is there with its
					transcript, and anyone on your team can take over a chat.
					Photos and voice notes shoppers send are served only to your
					signed-in team, never through a public link. Each retailer's
					data is kept apart by organization in every query. At
					Hephon, production access is limited to named staff, and
					admin actions are logged.
				</p>

				<h2 id="credentials">How are our credentials protected?</h2>
				<p>
					Platform credentials, WhatsApp tokens and webhook secrets
					are encrypted with AES-256-GCM, each bound to the record it
					belongs to, so a value copied to another record will not
					open. They are never shown again after you save them. API
					keys you create are scoped to what each one is for. Your app
					and website open the assistant with a launch token your
					server signs, and you can refuse every chat that does not
					have one. Only the websites you allow can frame the chat or
					talk to it.
				</p>

				<h2 id="retention">
					How long is data kept, and can it be deleted?
				</h2>
				<p>
					You set the retention period, from 7 days to 10 years; it is
					365 days by default. A daily job deletes conversations older
					than that, with their messages, baskets, checkouts and
					calls. Phone numbers and email addresses in shopper messages
					are masked in the stored copy by default. Call recording is
					off unless you turn it on.
				</p>
				<p>
					A shopper can ask for their data to be deleted. Your team
					makes the request in the dashboard, or your systems send it
					through the API, and it removes the shopper's conversations,
					profile and sessions. You can follow each request, and your
					server receives a webhook when it is done.
				</p>

				<h2 id="rules">How are product rules enforced?</h2>
				<p>
					On the server, never by the prompt alone. A retailer can
					hide alcohol, pork or tobacco, or all three for a Halal
					store. Each product is judged by an AI model, not a word
					list, and our server removes the hidden ones before the
					assistant sees the catalog, on every channel. A product that
					has not been checked yet stays hidden. On WhatsApp, alcohol
					and tobacco are always excluded. Only Hephon staff can
					loosen a restriction once it is live.
				</p>
				<p>
					Orders follow the same idea. No tool the assistant can call
					places an order. An order is placed only by the shopper's
					tap on a checkout, a signed checkout page, or keypad 1 after
					the basket is read back on a call.
				</p>

				<h2 id="pdpl">
					What about the PDPL in Saudi Arabia and the UAE?
				</h2>
				<p>
					We do not claim PDPL compliance: it depends on the whole
					processing, including yours. We explain how Hephon Commerce
					handles data under each law, and go through your
					requirements with your team before launch. The details, with
					dated sources, are on{" "}
					<a href="/security/pdpl">PDPL and your shoppers' data</a>.
				</p>

				<h2 id="not-claim">What we do not claim</h2>
				<ul>
					<li>
						<strong>No certification.</strong> Hephon Commerce holds
						no ISO 27001 or SOC 2 certification.
					</li>
					<li>
						<strong>No compliance badge.</strong> We do not claim
						Saudi or UAE PDPL compliance.
					</li>
					<li>
						<strong>No local hosting today.</strong> Data is not
						stored in Saudi Arabia or the UAE, and some AI providers
						are in the United States.
					</li>
					<li>
						<strong>Legal pages are drafts.</strong> The data
						processing agreement, subprocessors list, privacy policy
						and terms below are drafts pending legal review.
					</li>
				</ul>
			</Article>

			<Section id="privacy-title" title="Shopper privacy, built in.">
				<DetailGrid items={PRIVACY} />
				<ul className="mt-14 flex flex-wrap gap-x-8 gap-y-3">
					{LEGAL.map((item) => (
						<li key={item.href}>
							<a
								href={item.href}
								className={mkLink("text-[16px]")}
							>
								{item.label}
								<Chevron />
							</a>
						</li>
					))}
				</ul>
			</Section>

			<Section
				id="steps-title"
				tone="mist"
				title="A security review in three steps."
				center
			>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="Security, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
